import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { useTranslation } from '@ibk/shared-translation';
import { Button, Loading, Modal } from '@ibk/shared-ui';
import { operatorDrawerErrorAtom, operatorDrawerNavigationCleared, operatorDrawerSuccessAtom } from '@ibk/shared-utils';
import { useDrawerOperator } from '../DrawerOperator.hooks';
import { operatorsApi } from '../services/operatorsApi';
export const __operatorListDeleteOnConfirmRef = {
    current: null,
};
export function useOperatorsList() {
    const { t } = useTranslation();
    const selectedAccount = getSelectedAccountInfo();
    const selectedAccountPartyId = selectedAccount?.targetId;
    const { states } = useDrawerOperator();
    const shouldRefetch = useAtomValue(operatorDrawerNavigationCleared);
    const isDrawerOpen = states.operatorDrawerVisible;
    const { data, isLoading, isError, refetch, isFetching } = operatorsApi.endpoints.getOperators.useQuery({
        partyId: selectedAccountPartyId || '',
    }, {
        skip: !isDrawerOpen || !selectedAccountPartyId,
        refetchOnMountOrArgChange: shouldRefetch,
        refetchOnFocus: shouldRefetch,
        refetchOnReconnect: shouldRefetch,
    });
    const operatorsList = Array.isArray(data) ? data : (data?.result ?? []);
    const [deleteOperatorMutation] = operatorsApi.endpoints.deleteOperator.useMutation();
    const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
    const [pendingDelete, setPendingDelete] = React.useState(null);
    const setSuccessMessage = useSetAtom(operatorDrawerSuccessAtom);
    const setErrorMessage = useSetAtom(operatorDrawerErrorAtom);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const performDelete = async (payload) => {
        const fromParty = payload.partyId;
        const toParty = selectedAccount?.targetId || '';
        const accountId = selectedAccount?.accountId || undefined;
        if (!fromParty) {
            console.warn('Missing required fromParty identifier to delete operator', { fromParty });
            return;
        }
        try {
            await deleteOperatorMutation({
                fromParty,
                toParty,
                ...(accountId && { accountId }),
                ...(payload.roleId && { roleId: payload.roleId }),
            }).unwrap();
            setSuccessMessage(t('operator.drawer.pages.toast-remove-operator-success'));
            refetch();
        }
        catch (error) {
            setErrorMessage(t('operator.drawer.pages.toast-remove-operator-error'));
        }
    };
    const deleteOperator = async (payload) => {
        setPendingDelete(payload);
        setIsConfirmOpen(true);
    };
    const closeConfirm = () => {
        setIsConfirmOpen(false);
        setPendingDelete(null);
    };
    const onConfirm = async () => {
        if (!pendingDelete)
            return;
        setIsDeleting(true);
        try {
            await performDelete(pendingDelete);
        }
        finally {
            setIsDeleting(false);
            closeConfirm();
        }
    };
    if (process.env.NODE_ENV === 'test') {
        __operatorListDeleteOnConfirmRef.current = onConfirm;
    }
    const confirmationModal = (_jsx(Modal, { isOpen: isConfirmOpen, onClose: closeConfirm, title: t('operator.drawer.pages.home.confirm-remove-operator'), renderInPortal: true, children: _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 16 }, children: [_jsx("p", { children: t('operator.drawer.pages.home.confirm-remove-operator-subtext') }), _jsxs("div", { style: { display: 'flex', gap: 12, justifyContent: 'flex-end' }, children: [_jsx(Button, { variant: 'secondary', size: 'medium', dataTestId: 'confirm-delete-cancel', onClick: closeConfirm, disabled: isDeleting, children: t('cancel') }), _jsx(Button, { variant: 'primary', size: 'medium', dataTestId: 'confirm-delete-confirm', onClick: onConfirm, disabled: isDeleting, children: isDeleting ? _jsx(Loading, { size: 'micro' }) : t('operator.drawer.pages.home.confirm-remove-operator-button') })] })] }) }));
    return {
        operatorsList,
        isLoading,
        isRefetching: isFetching && !isLoading,
        isError,
        refetch: async () => {
            await refetch();
        },
        deleteOperator,
        confirmationModal,
        t,
    };
}
